import { useState, useEffect, useRef } from "react";

interface UseAdbShellWebSocketProps {
  uri: string;
  devId: string;
}

export interface ShellOutputEntry {
  command: string;
  stdout?: string;
  stderr?: string;
  exitCode?: number;
  success: boolean;
  error?: string;
}

export const useAdbShellWebSocket = ({
  uri,
  devId,
}: UseAdbShellWebSocketProps) => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState<string>("");
  const [outputHistory, setOutputHistory] = useState<ShellOutputEntry[]>([]);
  const [isExecuting, setIsExecuting] = useState(false);

  const ws = useRef<WebSocket | null>(null);
  const keepAliveInterval = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const connectedRef = useRef(false);

  const clearKeepAlive = () => {
    if (keepAliveInterval.current) {
      clearInterval(keepAliveInterval.current);
      keepAliveInterval.current = null;
    }
  };

  const clearConnectionTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const start = () => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      return;
    }

    setLoadingMessage("Connecting to device...");
    setError(null);
    setOutputHistory([]);

    try {
      ws.current = new WebSocket(uri);

      timeoutRef.current = setTimeout(() => {
        if (!connectedRef.current) {
          setError("Connection timeout - device did not respond");
          setLoadingMessage("");
          stop();
        }
      }, 30000);

      ws.current.onopen = () => {
        clearConnectionTimeout();

        if (ws.current) {
          ws.current.send(JSON.stringify({ admin: devId }));
        }

        connectedRef.current = true;
        setIsConnected(true);
        setLoadingMessage("");

        keepAliveInterval.current = setInterval(() => {
          if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send("keep-alive");
          }
        }, 10000);
      };

      ws.current.onmessage = (event) => {
        const raw = event.data;
        console.log("[AdbShell] Received message:", raw);

        if (raw === "keep-alive" || raw === "finish") {
          return;
        }

        try {
          const data = JSON.parse(raw);

          if (data.status === "ready") {
            setLoadingMessage("");
            connectedRef.current = true;
            setIsConnected(true);
            return;
          }

          // Handle shell command response
          if (
            data.command !== undefined ||
            data.success !== undefined ||
            data.stdout !== undefined
          ) {
            const entry: ShellOutputEntry = {
              command: data.command || "(unknown)",
              stdout: data.stdout,
              stderr: data.stderr,
              exitCode: data.exitCode,
              success: data.success ?? false,
              error: data.error,
            };
            setOutputHistory((prev) => [...prev, entry]);
            setIsExecuting(false);
            setError(null);
            return;
          }

          console.log("[AdbShell] Unhandled JSON message:", data);
        } catch {
          // Non-JSON message — show as raw output
          console.log("[AdbShell] Non-JSON message:", raw);
          setOutputHistory((prev) => [
            ...prev,
            {
              command: "",
              stdout: raw,
              success: true,
            },
          ]);
          setIsExecuting(false);
        }
      };

      ws.current.onerror = () => {
        setError("Connection error occurred");
        setLoadingMessage("");
        setIsExecuting(false);
      };

      ws.current.onclose = () => {
        connectedRef.current = false;
        setIsConnected(false);
        setLoadingMessage("");
        clearKeepAlive();
        clearConnectionTimeout();
      };
    } catch (err: any) {
      setError(err.message || "Failed to connect");
      setLoadingMessage("");
    }
  };

  const stop = () => {
    clearKeepAlive();
    clearConnectionTimeout();

    if (ws.current) {
      if (ws.current.readyState === WebSocket.OPEN) {
        ws.current.send("finish");
      }
      ws.current.close();
      ws.current = null;
    }

    connectedRef.current = false;
    setIsConnected(false);
    setLoadingMessage("");
    setIsExecuting(false);
  };

  const sendCommand = (command: string) => {
    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
      setError("Not connected to device");
      return;
    }

    if (!command.trim()) {
      return;
    }

    setIsExecuting(true);
    setError(null);

    try {
      ws.current.send(command);
    } catch (err: any) {
      setError(err.message || "Failed to send command");
      setIsExecuting(false);
    }
  };

  const clearHistory = () => {
    setOutputHistory([]);
  };

  useEffect(() => {
    return () => {
      stop();
    };
  }, []);

  return {
    start,
    stop,
    sendCommand,
    isConnected,
    error,
    loadingMessage,
    outputHistory,
    isExecuting,
    clearHistory,
  };
};
